import { combineLatest } from 'rxjs';
import { environment } from '@environment';

import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AppRoutes, DynamicTranslationService, FundService as CoreFundService } from '@gs-2.0/core';
import { ConfirmationDialogComponent } from '@gs-2.0/shared';

import { EnvironmentNames } from '../constants/environments';
import FundTypes from '../constants/fund-types';
import { Modals } from '../constants/modals';
import Urls from '../constants/urls';
import StorageHelper from '../helpers/storage.helper';
import { Fund } from '../models/fund';
import { FundService } from '../services/fund.service';
import { ModalsService } from '../services/modals.service';

@Injectable()
export class CanAccessGuard implements CanActivate {
  fund: Fund;

  // prettier ignore
  constructor(
    private fundService: FundService,
    private modalsService: ModalsService,
    private translateService: DynamicTranslationService,
    private router: Router
  ) {
    this.fundService.fund$.subscribe((fund: Fund) => {
      this.fund = fund;
    });
  }

  canActivate(_: ActivatedRouteSnapshot, __: RouterStateSnapshot) {
    const currentFund = this.fund || StorageHelper.getCurrentFund();
    if (!currentFund) {
      this.router.navigate([Urls.APP]);
      return false;
    }
    const currencyId: string =
      environment.fund.environmentName !== EnvironmentNames.SWISS
        ? currentFund.primaryCurrencyId
        : currentFund.receiveGrantCurrency;
    const currencySymbol: string =
      environment.fund.environmentName !== EnvironmentNames.SWISS
        ? currentFund.primaryCurrencySymbol
        : currentFund.receiveGrantCurrencySymbol;
    const noReceivingGrantsCurrency: boolean = [null, undefined, '0'].includes(currencyId);

    let popupMessage: string;

    if (noReceivingGrantsCurrency) popupMessage = 'DISTRIBUTION_WARNING_POPUP_NO_RECEIVING_CURRENCY';

    if (currentFund.fundType === FundTypes.CHARITY && popupMessage) {
      combineLatest([
        this.translateService.translate('DISTRIBUTION_WARNING_POPUP_TITLE', {
          currency: currencySymbol,
          email: environment.fund.mailTo,
        }),
        this.translateService.translate(popupMessage, {
          currency: currencySymbol,
          email: environment.fund.mailTo,
        })
      ]).subscribe(([title, message]) => {
        this.modalsService.openModal(Modals.DYNAMIC_CONTENT_MODAL, {
          displayName: title,
          infoMessage: message,
        });
      });
      return false;
    }
    return true;
  }
}

export const canAccessGuard: CanActivateFn = () => {
  const coreFundService = inject(CoreFundService);
  const dialog = inject(MatDialog);
  const router = inject(Router);

  const currentFund = coreFundService.selectedFund();

  // If fund isn't loaded yet, allow navigation — the component will validate
  if (!currentFund) {
    return true;
  }

  // Expired funds cannot distribute — redirect to dashboard silently
  const charityStatus = currentFund.charityStatus;
  if (charityStatus && charityStatus.toLowerCase().includes('expired')) {
    router.navigate([AppRoutes.FUND_DASHBOARD]);
    return false;
  }

  const currencyId: string =
    environment.fund.environmentName !== EnvironmentNames.SWISS
      ? currentFund.primaryCurrencyId
      : currentFund.receiveGrantCurrency;
  const noReceivingGrantsCurrency: boolean = [null, undefined, '0'].includes(currencyId);

  if (currentFund.fundType === FundTypes.CHARITY && noReceivingGrantsCurrency) {
    const dialogRef = dialog.open(ConfirmationDialogComponent, {
      width: '512px',
      maxWidth: '90vw',
      panelClass: ['confirmation-dialog-container'],
      data: {
        actionType: 'confirm',
        layout: 'detailed',
        title: 'Distribution Information',
        description: 'distribution.warning.receiving_grant_currency',
        params: { adminEmail: environment.fund?.mailTo },
        confirmButtonText: 'common.ok',
        hideCancelButton: true,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      router.navigate([`${Urls.FUND}/${Urls.DASHBOARD}`]);
    });
    return false;
  }
  return true;
};
